<template>
  <AppAccordion
    classes="border-sm bg-surface-primary mb-24"
    header-classes="bg-surface-tertiary py-16 px-16"
    :rounded-corners="true"
    border-color="tertiary"
    :only-mobile="false"
    :is-open="true"
  >
    <template #header>
      <div class="flex flex-row gap-16 items-center">
        <nuxt-icon name="coin" class="text-[3.6rem]" aria-hidden="true" />
        <div class="flex flex-col gap-8">
          <h2 class="heading-sm">{{ header }}</h2>
          <p v-if="ingress">
            {{ ingress }}
          </p>
        </div>
      </div>
    </template>
    <div class="w-full border-t-sm">
      <div class="p-16 flex flex-col lg:flex-row gap-24">
        <div class="w-full lg:w-1/2">
          <InputRadioGroup type-class="default" column-class="one-column">
            <InputRadioButton
              v-for="plan in plans"
              :id="`plan-${plan?.ProductId.toString()}`"
              :key="`${plan?.ProductId}`"
              v-model="selectedId"
              :value="plan?.ProductId.toString()"
              :simple-label="false"
              :group-name="type === 'fastpris' ? 'plansFastpris' : 'plansSaldo'"
            >
              <template #name-advanced>
                <div v-if="type === 'fastpris'">
                  <h3 v-if="plan?.ProductName" class="heading-xs">
                    {{ plan?.ProductName }}
                  </h3>
                  <p class="heading-xxs">
                    {{
                      plan?.ProductName === FASTPRIS_1GB
                        ? '200 min samtal'
                        : 'Fria samtal'
                    }}
                  </p>
                  <p class="heading-xxs">
                    {{
                      plan?.ProductName === FASTPRIS_1GB
                        ? '2000 SMS/MMS'
                        : 'Fria SMS/MMS'
                    }}
                  </p>
                </div>
                <div v-else-if="type === 'saldo'">
                  <h3 v-if="plan?.ProductName" class="heading-xxs">
                    {{ plan.ProductName }}
                  </h3>
                  <div v-if="plan?.ProductName">
                    <p>
                      {{
                        saldoAdditionalTextRows.get(plan.ProductName)
                          ?.initialPriceText
                      }}
                    </p>
                    <p>
                      {{
                        saldoAdditionalTextRows.get(plan.ProductName)?.surfText
                      }}
                    </p>
                    <p>
                      {{
                        saldoAdditionalTextRows.get(plan.ProductName)
                          ?.callPricePerMinuteText
                      }}
                    </p>
                    <p>
                      {{
                        saldoAdditionalTextRows.get(plan.ProductName)
                          ?.smsMmsText
                      }}
                    </p>
                  </div>
                </div>
              </template>
              <template #price-advanced>
                <p
                  v-if="
                    `${plan?.Variants?.at(0)?.Prices.BundlePrices?.at(0)?.Plan?.OneTimeFee}`
                  "
                  class="heading-xxs"
                >
                  {{
                    `${plan?.Variants?.at(0)?.Prices.BundlePrices?.at(0)?.Plan?.OneTimeFee} kr`
                  }}
                </p>
              </template>
            </InputRadioButton>
          </InputRadioGroup>
        </div>
        <div class="w-full lg:w-1/2 flex flex-col gap-16 justify-end">
          <AppButton
            button-type="button"
            :full-width="true"
            @click="goToCheckout(selectedId)"
            @keyup.enter.self.prevent="goToCheckout(selectedId)"
            @keyup.space.self.prevent="goToCheckout(selectedId)"
          >
            Beställ</AppButton
          >
          <p v-if="fastPrisText" v-html="fastPrisText"></p>
        </div>
      </div>
    </div>
  </AppAccordion>
</template>

<script lang="ts" setup>
import { markup_markdownLikeTextToHtmlString } from '~/utils/markup/allTextTransformations';
import type { AddBundleToCartRequestModel } from '~/models/shopping_cart/AddBundleToCartRequest.api.model';
import type { ListingProduct } from '~/models/ListingProduct.api.model';
const notifications = useNotificationStore();
const { $api } = useNuxtApp();
const prePaidStore = usePrePaidStore();
const { pushAddToCartEvent } = useGoogleAnalyticsEvents();

interface Props {
  header: string;
  ingress?: string;
  text?: string;
  plans: ListingProduct[];
  selectedPlanId: string;
  type: 'fastpris' | 'saldo';
}
const props = defineProps<Props>();

const DEFAULT_INITIAL_PRICE_TEXT = 'Öppningsavg 0,99 kr';
const DEFAULT_CALLS_PER_MIN_TEXT = 'Samtal 0,99 kr/samtalsminut';
const DEFAULT_SMS_MMS_TEXT = 'SMS/MMS 0,99 kr/SMS & MMS';
const FASTPRIS_1GB = 'Fastpris 1 GB';
const SALDO300 = '300';
const SALDO200 = '200';
const SALDO100 = '100';

const selectedId = ref(props.selectedPlanId);

const fastPrisText = props.text
  ? markup_markdownLikeTextToHtmlString(props.text)
  : undefined;

type SaldoAdditionalTextRow = {
  initialPriceText: string;
  surfText: string;
  callPricePerMinuteText: string;
  smsMmsText: string;
};

const saldoAdditionalTextRows = computed(() => {
  const newMap = new Map<string, SaldoAdditionalTextRow>();

  if (props.type === 'saldo' && props.plans?.length > 0) {
    props.plans.forEach((plan) => {
      const initialPriceText = DEFAULT_INITIAL_PRICE_TEXT;
      let surfText = '';
      const callPricePerMinuteText = DEFAULT_CALLS_PER_MIN_TEXT;
      const smsMmsText = DEFAULT_SMS_MMS_TEXT;

      if (plan?.ProductName.includes(SALDO300)) {
        surfText = 'Surf 2GB';
      } else if (plan?.ProductName.includes(SALDO200)) {
        surfText = 'Surf 1GB';
      } else if (plan?.ProductName.includes(SALDO100)) {
        surfText = 'Surf -';
      }

      newMap.set(plan.ProductName, {
        initialPriceText,
        surfText,
        callPricePerMinuteText,
        smsMmsText,
      });
    });
  }

  return newMap;
});

const goToCheckout = async (selectedId: string) => {
  prePaidStore.setPlanId(selectedId);
  const bundle = props.plans
    .filter((plan) => plan?.ProductId.toString() === selectedId)?.[0]
    ?.Variants?.at(0)
    ?.Prices.BundlePrices?.at(0);

  if (bundle?.BundlePriceId) {
    try {
      const requestPayload: AddBundleToCartRequestModel = {
        BundlePriceId: +bundle?.BundlePriceId,
        DownPayTime: 1,
      };
      await $api.ShoppingCartModule.AddBundleToCart(requestPayload);
      pushAddToCartEvent(prePaidStore.trackingData);
      navigateTo('/shop/varukorg', { external: true });
    } catch (e) {
      notifications.setStandardErrorNotification();
    }
  }
};
</script>
